import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import ContactFormModal from '../components/ContactFormModal';
import { Trans } from 'react-i18next';
import Reveal from '../components/Reveal';
import i18n from '../i18n';
import heroVideo from '../assets/Hero-Video3.mp4';
import pic2 from '../assets/61-time.png';
import pic3 from '../assets/3-strong-holding-earth.png';
import pic5 from '../assets/FusionX2.png';
import ideaImage from '../assets/38-idea.png';
import winnersVideo from '../assets/winners.mp4';
import winnersPoster from '../assets/winners2.jpg';
import dopamineBoostVideo from '../assets/Dopamin-boost.mp4';
import dopamineBoostPoster from '../assets/Dopamin-boost.png';
import highscoresVideo from '../assets/highscores.mp4';
import highscoresPoster from '../assets/highscores.jpg';
import menevaVideo from '../assets/meneva.mp4';
import menevaPoster from '../assets/meneva.jpg';

const LINKS = {
  codespace: 'https://codespace.fi',
  privacy: 'https://codespace.fi/fusionx/saas/public/privacy-policy.php',
  terms: 'https://codespace.fi/fusionx/saas/public/terms.php',
};

const LinkInline = ({ href, children, className = '' }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`text-purple-400 underline hover:text-purple-300 ${className}`}
  >
    {children}
  </a>
);

const manifestoLines = [
  'STOP interrupting. → Engage.',
  'STOP buying attention. → Earn it.',
  'STOP forcing ads. → Earn the right to show them.',
  'STOP renting media. → BECOME ONE.',
];

const whyItMatters = [
  {
    title: 'Attention is rented, engagement is owned',
    body: 'For years, brands have paid to interrupt, chasing impressions, clicks, and views that vanish in seconds. Play2Visit breaks that cycle by creating a space where engagement lives on, long after the ad ends.',
    direction: 'left',
  },
  {
    title: 'Your app or store becomes your own media',
    body: 'Instead of renting space from others, Play2Visit helps you turn your own digital channels into media that entertains, activates, and converts.',
    direction: 'right',
  },
  {
    title: 'Playable means measurable',
    body: 'Unlike traditional content, playable experiences generate real data, loyalty, and repeat interaction. Every move, score, and visit tells you not just who clicked, but who truly cares.',
    direction: 'left',
  },
  {
    title: 'From moments to movements',
    body: 'We turn campaigns into ongoing experiences. Every play builds brand memory. Every tournament drives store visits. Every engagement adds value that lasts.',
    direction: 'right',
  },
];

const steps = [
  {
    label: '1️⃣',
    title: 'From post to play',
    copy: 'Traffic starts from creator content, paid social, email, or QR. Players land in your app or online store and start playing, no friction.',
    video: menevaVideo,
    poster: menevaPoster,
  },
  {
    label: '2️⃣',
    title: 'FusionX Tournaments',
    copy: 'Players compete in weekly tournaments. Scores, streaks, and boosters keep the experience engaging and brand-integrated.',
    video: dopamineBoostVideo,
    poster: dopamineBoostPoster,
  },
  {
    label: '3️⃣',
    title: 'In-store scoreboards & rewards',
    copy: 'When players visit selected locations in the real world, the game unlocks a scoreboard view revealing rankings and rewards.',
    video: highscoresVideo,
    poster: highscoresPoster,
  },
  {
    label: '4️⃣',
    title: 'Earned ad moments',
    copy: 'Ad views appear at player-chosen moments (e.g., checking results or unlocking perks). We don’t force ads, we earn the right to show them.',
    video: winnersVideo,
    poster: winnersPoster,
  },
];

const paragraphsAbout = [
  'Traditional engagement ends when the ad does. Play2Visit changes that.',
  <>
    We help brands transform passive attention into active and lasting interaction. By bringing playable, rewarding experiences directly
    into your <span className="font-semibold text-purple-200">app</span> or <span className="font-semibold text-purple-200">online platform</span>, Play2Visit keeps your brand alive even when customers aren’t buying.
  </>,
  'It’s content marketing that goes beyond the scroll: interactive, measurable, and truly engaging.',
  'Why rent media when your own digital channels can become the destination your audience actually wants to spend time with?',
];

const teamMembers = [
  {
    key: 'kimmo',
    titleLine: '🎮 Kimmo Pennanen',
    role: 'Founder & Game Designer',
    paragraphs: [
      'A creative mind behind award-winning games and indie innovation.',
      'Kimmo designs and invents experiences that make people play, and keep them coming back.',
      'His passion for game mechanics, flow and human motivation is at the core of Play2Visit’s magic.',
    ],
    quote: '“Every great game starts with understanding what makes people stay.”',
    image: '/kimmo.png',
    accent: 'purple',
  },
  {
    key: 'arto',
    titleLine: '🚀 Arto Peisa',
    role: 'Strategic Advisor',
    paragraphs: [
      'A strategist who bridges marketing and play.',
      'Initially introduced to Play2Visit as a CMO at Menevä, Finland’s largest ride service, Arto quickly saw its wider potential for brands.',
      'He joined as an advisor to help shape the concept and bring a marketer’s perspective into the innovation, turning engagement into measurable results.',
    ],
    quote: '“We don’t just make games. We build engagement that drives visits.”',
    image: '/arto.png',
    accent: 'pink',
  },
];

const teamAccentStyles = {
  purple: {
    border: 'border-purple-400',
    text: 'text-purple-300',
  },
  pink: {
    border: 'border-pink-400',
    text: 'text-pink-300',
  },
};

const Play2Visit = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHeroReady, setIsHeroReady] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { lng } = useParams();

  useEffect(() => {
    if (lng && i18n.language !== lng) {
      i18n.changeLanguage(lng);
    }
  }, [lng]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = (event) => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const handleHeroTimeUpdate = (event) => {
    const video = event.currentTarget;
    if (!isHeroReady && video.duration && video.currentTime >= Math.max(video.duration - 2, 0)) {
      setIsHeroReady(true);
    }
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const leftTalkingPoints = whyItMatters.filter((_, index) => index % 2 === 0);
  const rightTalkingPoints = whyItMatters.filter((_, index) => index % 2 !== 0);

  return (
    <div className="bg-gradient-to-br from-gray-950 via-gray-900 to-black text-white font-inter min-h-screen overflow-x-hidden">
      {/* Hero */}
      <section
        className={`relative flex overflow-hidden ${
          isMobile ? 'flex-col items-center justify-start h-auto min-h-[90svh]' : 'items-center justify-center h-[90svh]'
        } md:h-screen`}
      >
        <video
          className={`transition-all duration-700 ${
            isMobile ? 'relative w-full h-auto max-h-[70vh] object-contain bg-black' : 'absolute inset-0 h-full w-full object-cover'
          }`}
          src={heroVideo}
          autoPlay
          playsInline
          muted
          onTimeUpdate={handleHeroTimeUpdate}
          onEnded={(event) => {
            event.currentTarget.pause();
            setIsHeroReady(true);
          }}
        />
        {isHeroReady && (
          <div
            className={`relative z-10 container mx-auto px-4 md:px-8 flex ${
              isMobile ? 'justify-center mt-6' : 'justify-center md:justify-start'
            }`}
          >
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className={`bg-gray-950/80 backdrop-blur-md rounded-3xl p-8 md:p-10 shadow-2xl max-w-3xl space-y-6 ${
                isMobile ? 'w-full' : ''
              }`}
            >
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: 'easeOut', delay: 0.2 }}
                className="text-4xl md:text-6xl font-bold leading-tight space-y-2"
              >
                <span className="block text-purple-300">
                  Stop renting attention, start owning engagement.
                </span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: 'easeOut', delay: 0.35 }}
                className="text-lg md:text-xl text-gray-200"
              >
                Turn your <span className="font-semibold text-purple-200">app or online store</span> into your own playable media that attracts customers online and sends them rushing to your{' '}
                <span className="font-semibold text-purple-200">real-world locations.</span>
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut', delay: 0.5 }}
                className="pt-2"
              >
                <button
                  onClick={openModal}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Book a demo!
                </button>
              </motion.div>
            </motion.div>
          </div>
        )}
      </section>

      {/* Section 1 */}
      {/*
      <section className="py-20 md:py-24 bg-gray-900/60">
        <div className="container mx-auto px-4 md:px-8 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div className="space-y-8">
            <Reveal direction="up" delay={0.1}>
              <div className="mx-auto w-2/3 aspect-[9/16] rounded-[2.5rem] border border-purple-500/40 bg-gray-950/60 p-4 text-center flex flex-col items-center justify-center overflow-hidden">
                <video
                  src={menevaVideo}
                  controls
                  playsInline
                  preload="metadata"
                  className="h-full w-full object-contain rounded-2xl"
                  aria-label="Menevä playable showcase"
                />
              </div>
            </Reveal>
          </div>
          <div className="space-y-6">
            <Reveal direction="left" delay={0.1}>
              <ul className="space-y-3 text-gray-200 text-lg">
                <li className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-purple-400" />
                  <span>
                    Engaging playable tournaments that{' '}
                    <span className="font-semibold text-purple-200">transform your app or platform into your own media.</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-purple-400" />
                  <span>A fun, brand-tailored game experience, keeping players spending time with your brand.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-purple-400" />
                  <span>
                    Scoreboards visible <span className="font-semibold text-purple-200">on-site</span> to drive measurable{' '}
                    <span className="font-semibold text-purple-200">visits.</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-purple-400" />
                  <span>
                    Turn screentime into <span className="font-semibold text-purple-200">real-world visits.</span>
                  </span>
                </li>
              </ul>
            </Reveal>
          </div>
        </div>
      </section>
      */}

      {/* Innovated in Finland */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-8 grid gap-10 lg:grid-cols-2 lg:items-center">
          <Reveal direction="left">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                INNOVATED IN FINLAND, <span className="text-purple-300">BUILT FOR THE WORLD</span>
              </h2>
              <p className="text-lg text-gray-200">
                Play2Visit is a digital innovation that bridges the gap between online interaction and in-store traffic, turning short-lived clicks into long-lasting engagement.
              </p>
              <p className="text-lg text-gray-200">
                Powered by world-class Finnish gaming expertise, this fun, engaging marketing tool empowers brands and retailers to convert online attention into measurable footfall and build strong, lasting customer relationships.
              </p>
            </div>
          </Reveal>
          <Reveal direction="right" delay={0.1}>
            <img
              src={ideaImage}
              alt="Innovation concept illustration"
              className="w-full rounded-2xl shadow-xl object-cover"
            />
          </Reveal>
        </div>
      </section>

      {/* Section 2 */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 md:px-8 grid gap-16 lg:grid-cols-[1.2fr_1fr] lg:items-start">
          <div className="space-y-6 order-2 lg:order-1">
            <Reveal direction="up">
              <img src={pic2} alt="Play2Visit campaign montage" className="w-full rounded-2xl shadow-xl object-cover" />
            </Reveal>
          </div>
          <div className="space-y-6 order-1 lg:order-2">
            <Reveal direction="right">
              <h2 className="text-3xl md:text-4xl font-bold text-white">About Play2Visit</h2>
            </Reveal>
            {paragraphsAbout.map((paragraph, index) => (
              <Reveal
                key={index}
                direction={index % 2 === 0 ? 'left' : 'right'}
                delay={index * 0.1}
              >
                <p className="text-lg md:text-xl text-gray-200">{paragraph}</p>
              </Reveal>
            ))}
            <Reveal direction="up" delay={0.4}>
              <p className="text-lg md:text-xl font-semibold text-purple-200">
                Play2Visit, from screentime to real-world visits.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Real Story */}
      <section className="py-20 md:py-24 bg-gray-950/70 border-y border-white/5">
        <div className="container mx-auto px-4 md:px-8">
          <Reveal direction="up">
            <div className="rounded-3xl border border-white/10 bg-gray-900/60 p-8 md:p-12 shadow-2xl space-y-5">
              <h2 className="text-3xl md:text-4xl font-bold text-white">REAL STORY, FROM MARKETER TO ADVISOR</h2>
              <div className="space-y-5 text-lg text-gray-200 leading-relaxed">
                <p>
                  "Play2Visit was first introduced to me as a marketer exploring new ways to activate our brand and app at{' '}
                  <a
                    href="https://meneva.fi/en"
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-white underline decoration-purple-300/60 decoration-dotted underline-offset-4 hover:text-purple-200"
                  >
                    Menevä
                  </a>
                  , Finland’s largest ride service.
                </p>
                <p>As I saw its potential, I realized it could solve far more than one brand’s challenge.</p>
                <p>
                  With different implementations, Play2Visit tackles the most common marketing challenges, from driving engagement to delivering measurable results.
                </p>
                <p>
                  That’s why I decided to join as an advisor: to help shape the concept further and bring the marketer’s perspective into this innovation."
                </p>
              </div>
              <p className="text-lg text-purple-200 italic">— Arto Peisa, CMO of Menevä</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Section 3 */}
      <section className="py-20 md:py-24 bg-gray-900/60">
        <div className="container mx-auto px-4 md:px-8">
          <div className="space-y-12 lg:space-y-0 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)_minmax(0,1fr)] lg:gap-12 lg:items-start">
            <div className="space-y-12 order-2 lg:order-1">
              {leftTalkingPoints.map((item, index) => (
                <Reveal key={item.title} direction={item.direction} delay={index * 0.1}>
                  <div className="lg:pr-6">
                    <h3 className="text-2xl md:text-3xl font-semibold text-white mb-3">{item.title}</h3>
                    <p className="text-lg text-gray-200">{item.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal direction="up" className="order-1 lg:order-2 flex justify-center">
              <img
                src={pic3}
                alt="Customers engaging with a brand experience"
                className="w-full max-w-sm md:max-w-md lg:max-w-xs xl:max-w-sm rounded-2xl shadow-xl object-cover"
              />
            </Reveal>
            <div className="space-y-12 order-3">
              {rightTalkingPoints.map((item, index) => (
                <Reveal key={item.title} direction={item.direction} delay={index * 0.1}>
                  <div className="lg:pl-6">
                    <h3 className="text-2xl md:text-3xl font-semibold text-white mb-3">{item.title}</h3>
                    <p className="text-lg text-gray-200">{item.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal direction="up" delay={0.4}>
            <p className="mt-16 text-center text-2xl font-semibold text-purple-200">
              Stop renting attention, start owning engagement.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Section 4 */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 md:px-8 space-y-12">
          <div className="grid gap-10 md:grid-cols-2">
            {steps.map((step, index) => (
              <Reveal key={step.title} direction="up" delay={index * 0.1}>
                <div className="mx-auto w-full max-w-[22rem]">
                  <div className="relative aspect-[9/19] rounded-[3rem] border border-purple-500/40 bg-gradient-to-b from-gray-900/80 to-black/80 p-3 shadow-[0_30px_50px_-35px_rgba(139,92,246,0.8)]">
                    <div className="flex h-full flex-col rounded-[2.5rem] border border-white/5 bg-gray-950/90 px-6 py-6">
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-500/25 text-xl">
                          {step.label}
                        </div>
                        <div>
                          <p className="text-[0.78rem] uppercase tracking-[0.4em] text-purple-300 font-semibold">Step {index + 1}</p>
                          <h3 className="text-lg font-semibold text-white leading-tight">{step.title}</h3>
                        </div>
                      </div>
                      {(step.image || step.video) && (
                        <div className="mt-4 flex flex-1 items-center justify-center rounded-3xl border border-white/5 bg-black/40 overflow-hidden">
                          {step.video ? (
                            <video
                              src={step.video}
                              poster={step.poster}
                              controls
                              playsInline
                              preload="metadata"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <img
                              src={step.image}
                              alt={`Illustration for ${step.title.toLowerCase()}`}
                              className="h-full w-full object-contain"
                            />
                          )}
                        </div>
                      )}
                      <p className="mt-4 text-sm text-gray-300 leading-relaxed">{step.copy}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal direction="up" delay={0.2}>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-xl border border-white/10 bg-gray-900/60 p-6 text-gray-200">
                <span className="font-semibold text-white">Easy integration</span> · Web embed for online stores and apps. Branded reskins without heavy dev work.
              </div>
              <div className="rounded-xl border border-white/10 bg-gray-900/60 p-6 text-gray-200">
                <span className="font-semibold text-white">Data & measurement</span> · Sessions, repeat play, store visits, redemptions, and tournament analytics, <span className="font-semibold text-purple-200">the metrics that matter to CMOs and growth teams.</span>
              </div>
            </div>
          </Reveal>
          <Reveal direction="up" delay={0.4}>
            <div className="text-center space-y-4">
              <p className="text-xl md:text-2xl font-semibold text-purple-200">
                Ready to turn screentime into store time?
              </p>
              <button
                onClick={openModal}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Book a demo!
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Section 5 */}
      <section className="py-20 md:py-24 bg-gray-900/70">
        <div className="container mx-auto px-4 md:px-8 text-center space-y-6">
          <Reveal direction="up">
            <img src={pic5} alt="Play2Visit manifesto collage" className="mx-auto w-full max-w-4xl rounded-2xl shadow-xl object-cover" />
          </Reveal>
          <div className="space-y-4">
            {manifestoLines.map((line, index) => (
              <Reveal key={line} direction="up" delay={index * 0.1}>
                <p className="text-xl md:text-2xl font-semibold text-gray-100">{line}</p>
              </Reveal>
            ))}
          </div>
          <Reveal direction="up" delay={0.4}>
            <p className="mt-6 text-3xl md:text-4xl font-bold text-purple-200">
              Play2Visit, from screentime to store time.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 md:py-24 bg-gray-900/80">
        <div className="container mx-auto px-4 md:px-8 space-y-12">
          <Reveal direction="up">
            <div className="text-center space-y-3">
              <h2 className="text-3xl md:text-4xl font-bold text-white">The team, agile minds, real results</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {teamMembers.map((member, index) => {
              const accent = teamAccentStyles[member.accent] || teamAccentStyles.purple;
              return (
                <Reveal key={member.key} direction={index % 2 === 0 ? 'left' : 'right'}>
                  <div className="bg-gray-900/60 p-8 rounded-3xl border border-white/10 shadow-2xl text-center flex flex-col items-center space-y-5">
                    <div className={`w-32 h-32 rounded-full border-4 ${accent.border} overflow-hidden`}>
                      <img src={member.image} alt={member.titleLine} className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-2xl font-bold text-white text-center">{member.titleLine}</h3>
                    <p className={`font-semibold ${accent.text}`}>{member.role}</p>
                    <div className="space-y-4 text-gray-300 text-left w-full">
                      {member.paragraphs.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                    </div>
                    <div className={`w-full bg-white/5 border-l-4 ${accent.border} p-4 rounded-r-2xl text-left`}>
                      <p className="text-gray-100 italic">{member.quote}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal direction="up">
            <div className="max-w-3xl mx-auto text-center space-y-4 bg-gray-900/60 border border-white/5 rounded-3xl p-8">
              <p className="text-2xl font-bold text-white">🤝 Together</p>
              <p className="text-lg text-gray-200">
                They combine the best of <span className="font-semibold text-white">gaming and marketing</span>, Finnish creativity and global vision —
                to build a platform where play moves people, and engagement becomes something you can own.
              </p>
              <p className="text-sm uppercase tracking-[0.35em] text-gray-400">Play2Visit, Innovated in Finland. Born global. Powered by Codespace.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="py-16 md:py-20 border-t border-gray-700/50 text-center">
        <p className="text-gray-500 text-sm">
          <Trans
            i18nKey="footer.line"
            values={{ year: new Date().getFullYear() }}
            components={{
              codespace: <LinkInline href={LINKS.codespace} />,
              privacy: <a href={LINKS.privacy} className="hover:text-white" />,
              terms: <a href={LINKS.terms} className="hover:text-white" />,
              sep: <span className="px-2" />,
            }}
          />
        </p>
      </footer>

      {isModalOpen && <ContactFormModal onClose={closeModal} />}
    </div>
  );
};

export default Play2Visit;
